
import { Vue, Component } from 'vue-property-decorator';
import DataTableWrapper from '@/features/common/components/DataTableWrapper.vue';
import UserItem from '@/features/users/components/UserItem.vue';
import AddUserModal from '@/features/users/components/AddUserModal.vue';
import EditUserModal from '@/features/users/components/EditUserModal.vue';
import DeleteUserModal from '@/features/users/components/DeleteUserModal.vue';
import NoPermission from '@/features/users/components/NoPermission.vue';

import { getUsers, resendInvitation } from '@/features/users/services/users.services';
import { UserInfo, UserItemType } from '@/features/users/types';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';

export interface TransferableUserType {
  userId: number,
  fullName: string,
  username: string
}

@Component({
  components: {
    DataTableWrapper,
    UserItem,
    AddUserModal,
    EditUserModal,
    DeleteUserModal,
    NoPermission,
  },
})
export default class Users extends Vue {
  private loading = false;

  private usersList: UserItemType[] = [];

  private selectedUser: UserItemType | null = null;

  private showDialogAdd = false;

  private showDialogEdit = false;

  private showDialogDelete = false;

  private isNoPermissionToView = false;

  get transferToUsers(): TransferableUserType[] {
    if (!this.selectedUser) return [];

    return this.usersList.filter(
      (u) => u.isTransferable && u.userId !== this.selectedUser?.userId,
    ).map((u) => {
      return {
        userId: u.userId,
        fullName: u.fullName,
        username: u.username,
      };
    });
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  async created(): Promise<void> {
    await this.fetchUsersList();
  }

  private async fetchUsersList() {
    this.loading = true;
    const usersResponse = await getUsers();
    this.loading = false;

    if (!usersResponse.successful && usersResponse.errorType === 'PermissionException') {
      this.isNoPermissionToView = true;
      this.usersList = [];
      return;
    }

    if (usersResponse.successful && usersResponse.data?.length) {
      this.isNoPermissionToView = false;
      this.usersList = usersResponse.data;
      return;
    }

    this.usersList = [];
  }

  private updateUser(user: UserItemType) {
    this.selectedUser = { ...user };
    this.showDialogEdit = true;
  }

  private deleteUser(user: UserItemType) {
    this.selectedUser = { ...user };
    this.showDialogDelete = true;
  }

  private onAddUser(user: UserItemType) {
    this.usersList = [user, ...this.usersList];
    showSuccessAlertDialog(
      this.$t('manageUsers.addUser.alert.successfulMessage', {
        firstName: user.firstName,
      }) as string,
    );
  }

  private onUpdateUser(user: UserItemType) {
    for (let i = 0; i < this.usersList.length; i += 1) {
      if (this.usersList[i].userId === user.userId) {
        this.$set(this.usersList, i, user);
        break;
      }
    }

    showSuccessAlertDialog(
      this.$t('manageUsers.editUser.alert.successfulMessage') as string,
    );

    this.updateUserInfoOnSidebar(user);
  }

  private onDeleteUser(user: UserItemType) {
    this.usersList = this.usersList.filter((u) => u.userId !== user.userId);
    showSuccessAlertDialog(
      this.$t('manageUsers.deleteUser.alert.successfulMessage') as string,
    );
  }

  private async onResendInvitation(user: UserItemType) {
    const response = await resendInvitation(user.userId);

    if (response.successful && response.resendInvitation) {
      showSuccessAlertDialog(
        this.$t('manageUsers.resendInvitation.alert.successfulMessage', {
          firstName: user.firstName,
        }) as string,
      );
      return;
    }

    const errorMessage = response.errorMessage
        || this.$t('alertDialog.somethingWentWrong') as string;
    showErrorAlertDialog(errorMessage);
  }

  private updateUserInfoOnSidebar(user: UserItemType) {
    const {
      userId: currentUserId,
      account,
      hasAcceptedLatestTerms,
    } = this.userInfo;

    const {
      userId: userIdFromResponse,
      firstName: firstname,
      lastName: lastname,
      fullName,
      username,
    } = user;

    if (currentUserId !== userIdFromResponse) return;

    this.$store.commit('setUserInfo', {
      userId: userIdFromResponse,
      firstname,
      lastname,
      fullName,
      username,
      account,
      hasAcceptedLatestTerms,
    });
  }
}
